import { createSlice } from '@reduxjs/toolkit'
import { DEFAULT_GROUP_TYPE } from '../../constant/GROUP_TYPES'
import {
  ciCoverageAmounts, disabilityCoverageAmounts,
  field,
  FORM_SEGMENTS, lifeCoverageAmounts, optionalField,
  requiredField
} from '../common/FORM_SEGMENTS'
import _ from 'lodash'
import REQUEST_STATUS from '../common/REQUEST_STATUS'
import nameFieldsToFullName from '../../util/nameFieldsToFullName'
import { fetchEoiRulesReducer } from '../reducers/fetchEoiRules'
import { validateCode1AddressReducer } from '../reducers/validateCode1Address'
import { fetchMemberByClaimKeyReducer } from '../reducers/fetchMemberByClaimKey'
import shallowCompareObject from '../../util/shallowCompareObject'
import { fetchMemberLiteReducer } from '../reducers/fetchMemberLite'
import allFieldsValid from '../../util/allFieldsValid'
import { validatePinReducer } from '../reducers/validatePin'
import { IMask } from 'react-imask/dist/react-imask'
import { CURRENCY_MASK_OPTIONS } from '../../constant/masks'
import { CI, HEALTH_INFORMATION_SEGMENTS } from '../../constant/HEALTH_INFORMATION_SEGMENTS'

const currencyMask = IMask.createMask({
  ...CURRENCY_MASK_OPTIONS.additionalProps,
  mask: Number
})

const initialFormSegmentsState = () => {
  return Object.keys(FORM_SEGMENTS).reduce((o, k) => {
    const { display, panel, ...filteredSegmentWithPropsNeededForState } = FORM_SEGMENTS[k]
    return { ...o, [k]: filteredSegmentWithPropsNeededForState }
  }, {})
}

export const initialFormState = {
  error: undefined,
  segments: initialFormSegmentsState(),
  context: {
    sessionLive: false, // if any field has been updated, start idle timer
    sessionKilled: false,
    prePopId: field,
    claimKey: field,
    appDataRequestStatus: REQUEST_STATUS.NOT_STARTED,
    childrenIncluded: field,
  },
  options: {
    applicantType: field,
    homeState: field,
    situsState: field,
    spouseOnly: optionalField,
    groupType: { ...field, value: DEFAULT_GROUP_TYPE },
    life: { ...field, value: false },
    disability: { ...field, value: false },
    ci: { ...field, value: false }
  }
}

export const isValid = (field) => {
  if (!field.dirty) {
    return true
  }

  if (!field.required && !field.value) {
    return true
  }

  let regex = field.pattern
  const isDateField = field.maskOptions?.pattern === '00/00/0000'
  if (isDateField) {
    return isValidDate(field.value)
  } else {
    // A field can have a masked input pattern without being required. Do not validate against the mask pattern unnecessarily.
    if (field.required && field.maskOptions?.pattern) {
      regex = `^${field.maskOptions.pattern
        .replace(/\//g, '\\/')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)')
        .replace(/\d/g, '\\d')}`
    }
    return regex ? field.value.toString().match(regex)?.length > 0 : true
  }
}

function isValidDate (dateString) {
  const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/([0-9]{4})$/
  return regex.test(dateString)
}

export const updateHealthQuestionAndCoverageAmountFields = (state) => {
  updateCoverageAmountFields(state)
  setHealthQuestionsFieldsForApplicantType(state)
  setPregnancyQuestion(state)
  updateRequiredSignatures(state)
}

const setHealthQuestionsFieldsForApplicantType = (state) => {
  const eeField = state.options.applicantType.value !== 'SP_ONLY' ? requiredField : {}
  const spField = state.segments.dependents.fields.spouseApplying.value === 'yes' ? requiredField : {}
  const chField = state.context.childrenIncluded && state.segments.dependents.fields.childrenApplying.value === 'yes' ? requiredField : {}
  HEALTH_INFORMATION_SEGMENTS.filter(s => state.segments[s]?.enabled).forEach(h => {
    ['healthQuestions', 'healthInsuranceQuestions'].forEach(grouping => {
      let questionGroup = state.segments[h].fields[grouping]
      if (questionGroup) {
        Object.keys(questionGroup).filter(q => questionGroup[q].type !== 'choice').forEach(questionId => {
          if (questionGroup[questionId].category !== 'OB_GYN') {
            questionGroup[questionId]['employee'] = !shallowCompareObject(questionGroup[questionId]['employee'], eeField) ? eeField : questionGroup[questionId]['employee']
            questionGroup[questionId]['spouse'] = !shallowCompareObject(questionGroup[questionId]['spouse'], spField) ? spField : questionGroup[questionId]['spouse']
            questionGroup[questionId]['children'] = h !== CI && !shallowCompareObject(questionGroup[questionId]['children'], chField) ? chField : questionGroup[questionId]['children']
          }
        })
      }
    })
  })
}

const updateCoverageAmountFields = (state) => {
  const lifeOnly = {
    ...(state.options.life.value && lifeCoverageAmounts)
  }
  const withCi = {
    ...lifeOnly,
    ...(state.options.ci.value && ciCoverageAmounts)
  }
  const withDisability = {
    ...withCi,
    ...(state.options.disability.value && disabilityCoverageAmounts)
  }

  Array.of(lifeOnly, withCi, withDisability).forEach(o => {
    if (!_.isEmpty(o)) {
      o.include = true
    }
  })

  if (state.options.applicantType.value === 'SP_ONLY') {

    if (state.options.life.value) {
      state.segments.coverageAmount.fields.employee.lifeTotal = {}
      state.segments.coverageAmount.fields.employee.lifeAdditional = {}
    }
    if (state.options.ci.value) {
      state.segments.coverageAmount.fields.employee.ciTotal = {}
      state.segments.coverageAmount.fields.employee.ciAdditional = {}
    }
  }

  state.segments.coverageAmount.fields.employee = !shallowCompareObject(state.segments.coverageAmount.fields.employee, withDisability) ?
    withDisability :
    state.segments.coverageAmount.fields.employee

  Array.of(
    { isApplyingField: 'spouseApplying', coverageAmountField: 'spouse', coverages: withCi },
    { isApplyingField: 'childrenApplying', coverageAmountField: 'children', coverages: lifeOnly }
  ).forEach(({ isApplyingField, coverageAmountField, coverages }) => {
    if (_.get(state, `segments.dependents.fields.${isApplyingField}.value`) === 'yes') {
      state.segments.coverageAmount.fields[coverageAmountField] = !shallowCompareObject(state.segments.coverageAmount.fields[coverageAmountField], coverages) ?
        coverages :
        state.segments.coverageAmount.fields[coverageAmountField]
    } else {
      state.segments.coverageAmount.fields[coverageAmountField] = {}
    }
  })
}

const updateRequiredSignatures = (state) => {
  _.set(state, 'segments.verification.fields.spouseElectronicSignature.required', state.segments.dependents.fields.spouseApplying.value === 'yes')
  updateSignaturePatterns(state)
  _.set(state, 'segments.verification.fields.spouseElectronicSignature.valid', state.segments.dependents.fields.spouseApplying.value !== 'yes')
}

const updateSignaturePatterns = (state) => {
  const pathForFieldObject = 'segments.verification.fields'
  _.set(
    state,
    'segments.verification.fields.employeeElectronicSignature.pattern',
    nameFieldsToFullName(state, 'segments.employee.fields') ||
    _.get(state, `${pathForFieldObject}.employeeElectronicSignature.pattern`)
  )
  _.set(
    state,
    'segments.verification.fields.spouseElectronicSignature.pattern',
    nameFieldsToFullName(state, 'segments.dependents.fields.spouse') ||
    _.get(state, `${pathForFieldObject}.spouseElectronicSignature.pattern`)
  )
  if (_.get(state, `${pathForFieldObject}.employeeElectronicSignature.value`)) {
    _.set(state, `${pathForFieldObject}.employeeElectronicSignature.valid`, isValid(_.get(state, `${pathForFieldObject}.employeeElectronicSignature`)))
  }
  if (_.get(state, `${pathForFieldObject}.spouseElectronicSignature.value`)) {
    _.set(state, `${pathForFieldObject}.spouseElectronicSignature.valid`, isValid(_.get(state, `${pathForFieldObject}.spouseElectronicSignature`)))
  }
}

const setPregnancyQuestion = (state) => {
  const showQuestion = state.options.applicantType.value !== 'SP_ONLY' &&
    state.segments.employee.fields.personal.gender.value === 'female'
  HEALTH_INFORMATION_SEGMENTS.filter(s => state.segments[s]?.enabled).forEach(h => {
    ['healthQuestions', 'healthInsuranceQuestions'].forEach(grouping => {
      let questionGroup = state.segments[h].fields[grouping]
      Object.keys(questionGroup || {}).filter(q => questionGroup[q].category === 'OB_GYN').forEach(q => {
        _.set(
          state.segments,
          `${h}.fields.${grouping}.${q}.hide`,
          !showQuestion
        )
        _.set(
          state.segments,
          `${h}.fields.${grouping}.${q}.employee`,
          showQuestion ? requiredField : {}
        )
      })
    })
  })
}

const setTotalCoverageAmounts = (state, pathParts, isLife) => {
  pathParts.pop()
  const path = pathParts.join('.')
  _.set(
    state,
    `${path}.${isLife ? 'life' : 'ci'}Total.value`,
    currencyMask.resolve(String(
      Number(
        _.get(state, `${path}.${isLife ? 'life' : 'ci'}Current.value`, 0)
          .replace(/\D/g, '')
      ) +
      Number(
        _.get(state, `${path}.${isLife ? 'life' : 'ci'}Additional.value`, 0)
          .replace(/\D/g, '')
      )
    ))
  )
}

const dirtyField = (field) => {
  if (_.isEmpty(field) || typeof field !== 'object') {
    return null
  } else if ('dirty' in field) {
    field.dirty = true
    field.valid = isValid(field)
  } else {
    return Array.isArray(field) ?
      field.forEach(subfield => dirtyField(subfield)) :
      Object.keys(field).forEach(subfield => dirtyField(field[subfield]))
  }
}

const validateAllAttemptedSegments = (state) => {
  Object.keys(state.segments)
    .filter(segment => state.segments[segment].attempted)
    .forEach(segment => {
      const valid = allFieldsValid(state.segments[segment].fields)
      state.segments[segment].completed = valid
      state.segments[segment].errored = !valid
    })
}
const isSpouseOnlyAndSpouseField = (state, pathParts) => {
  return isSpouseOnly && pathParts[3] === 'spouse'
}

const isSpouseOnly = (state) => {
  return state.options.spouseOnly.value === 'yes'
}

const formSlice = createSlice({
  name: 'form',
  initialState: initialFormState,
  reducers: {
    resetForm () {
      return initialFormState
    },
    onSessionKilled (state) {
      state.context.sessionKilled = true
      return state
    },
    /**
     * Certain form options and fields will affect who is required to answer health questions and coverage amount information.
     * Any time one of those options or fields are updated, ensure we have the correct required fields set for adjacent form segments.
     */
    updateApplicantRelatedFields (state) {
      updateHealthQuestionAndCoverageAmountFields(state)
    },
    onSegmentAttempted (state, action) {
      state.segments[action.payload.segment].attempted = true
      Object.keys(state.segments[action.payload.segment].fields).forEach(k => {
        dirtyField(state.segments[action.payload.segment].fields[k])
      })
      validateAllAttemptedSegments(state)
    },
    updateList (state, action) {
      switch (action.payload.type) {
        case 'append':
          _.set(
            state,
            action.payload.key,
            [..._.get(state, action.payload.key), action.payload.defaultValue]
          )
          break
        case 'remove':
          _.pullAt(_.get(state, action.payload.key), action.payload.index)
          break
        default:
          break
      }

      if (action.payload.pathForCount) {
        _.set(
          state,
          action.payload.pathForCount,
          _.get(state, action.payload.pathForCount) + (action.payload.type === 'append' ? 1 : -1)
        )
      }
    },
    setField (state, action) {
      if (!action.payload.safeUpdate || (action.payload.safeUpdate && !shallowCompareObject(action.payload.value, _.get(state, action.payload.key)))) {
        _.set(state, action.payload.key, action.payload.value)
      }
      if (action.payload.key.startsWith('options')) {
        switch (action.payload.key.split('.')[1]) {
          case 'applicantType':
            state.segments.dependents.enabled = action.payload.value !== 'EE_ONLY'
            state.options.disability.value = action.payload.value === 'SP_ONLY' ? false : state.options.disability.value
            state.segments.dependents.fields.spouseApplying.value = action.payload.value === 'SP_ONLY' ? 'yes' : ''
            state.segments.dependents.fields.spouseApplying.disabled = action.payload.value === 'SP_ONLY'
            state.segments.dependents.fields.childrenApplying.value = action.payload.value === 'SP_ONLY' ? 'no' : ''
            state.segments.dependents.fields.childrenApplying.disabled = action.payload.value === 'SP_ONLY'
            state.options.spouseOnly.value = action.payload.value === 'SP_ONLY' ? 'yes' : ''
            break
          case 'homeState':
            state.segments.employee.fields.address.state.value = action.payload.value
            state.segments.employee.fields.address.state.disabled = true
            state.segments.employee.fields.address.state.valid = isValid(state.segments.employee.fields.address.state)
            state.options.ci.value = ['PR', 'VI'].includes(action.payload.value) ? false : state.options.ci.value
            break
          case 'disability':
            if (action.payload.value && !state.options.life.value && !state.options.ci.value) {
              state.options.applicantType.value = 'EE_ONLY'
              state.options.applicantType.disabled = true
            } else {
              state.options.applicantType.disabled = false
            }
            break
          case 'life':
          case 'ci':
            if (!action.payload.value && state.options.disability.value && !state.options.life.value && !state.options.ci.value) {
              state.options.applicantType.value = 'EE_ONLY'
              state.options.applicantType.disabled = true
            } else {
              state.options.applicantType.disabled = false
            }
            break
          case 'situsState':
            state.segments.employer.fields.address.state.value = action.payload.value
            state.segments.employer.fields.address.state.disabled = true
            break
          default:
            break
        }
      } else {

        if (!action.payload.key.startsWith('context')) {
          state.context.sessionLive = true
        }

        if (typeof action.payload.value !== 'object') {
          let pathParts = action.payload.key.split('.')
          pathParts.pop()
          const pathForFieldObject = pathParts.join('.')

          if (action.payload.dirtyOnValueChange) {
            _.set(state, `${pathForFieldObject}.dirty`, true)
          }

          if (!action.payload.skipValidation) {
            _.set(state, `${pathForFieldObject}.valid`, isValid(_.get(state, pathForFieldObject)))
            _.set(state, `${pathForFieldObject}.disabled`, action.payload.disable)
          }

          const field = pathParts.slice(-1)[0]

          /**
           *  As the e-signature must equal the full name of the applicant,
           *    anytime a name changes we want to set the regex pattern to be matched against for the signatures.
           */
          if (['firstName', 'lastName', 'middleInitial'].includes(field)) {
            updateSignaturePatterns(state)
          }

          /**
           * If the applicant has selected Female gender, any health questions of category OB_GYN must be answered
           */
          if (field === 'gender' && pathParts[1] === 'employee') {
            setPregnancyQuestion(state)
          }

          if (['ciCurrent', 'ciAdditional'].includes(field)) {
            if (isSpouseOnlyAndSpouseField(state, pathParts)) {
              setTotalCoverageAmounts(state, pathParts, false)
            } else if (!isSpouseOnly(state)) {
              setTotalCoverageAmounts(state, pathParts, false)
            }

          } else if (['lifeCurrent', 'lifeAdditional'].includes(field)) {
            if (isSpouseOnlyAndSpouseField(state, pathParts)) {
              setTotalCoverageAmounts(state, pathParts, true)
            } else if (!isSpouseOnly(state)) {
              setTotalCoverageAmounts(state, pathParts, true)
            }
          }

          validateAllAttemptedSegments(state)
        }
      }
      return state
    }
  },
  extraReducers: {
    ...fetchMemberByClaimKeyReducer,
    ...fetchEoiRulesReducer,
    ...validateCode1AddressReducer,
    ...fetchMemberLiteReducer,
    ...validatePinReducer
  }
})

/**
 *
 * @param key The path at which the value should be set
 * @param value The value to be set
 * @param disable A field can be disabled upon setting the value
 * @param safeUpdate A field can check object keys only, not values,
 *    and ensure nested values are not being overwritten.
 * @param skipValidation Checking the pattern and validating the value can optionally be skipped.
 * @param dirtyOnValueChange A form field can be dirtied when it is first updated, instead of waiting
 *    for blur event. This is useful when wanting to immediately validate the input pattern, like e-signatures
 */
export const setField = (
  key,
  value,
  disable = false,
  safeUpdate = false,
  skipValidation = false,
  dirtyOnValueChange = false
) => formSlice.actions.setField({ key, value, disable, safeUpdate, skipValidation, dirtyOnValueChange })

/**
 *
 * @param key - The path at which the value should be set.
 * @param defaultValue - The default object to append to the list.
 * @param pathForCount - Optionally, a separate property can be provided and used for tracking a total count.
 *                       This is useful when maintaining a combined count across different object paths.
 */
export const appendList = (key, defaultValue, pathForCount) => formSlice.actions.updateList({
  key,
  defaultValue,
  type: 'append',
  pathForCount: pathForCount
})

/**
 *
 * @param key - The path at which the value should be set.
 * @param index - The array index at which to drop from the array.
 * @param pathForCount - Optionally, a separate property can be provided and used for tracking a total count.
 *                       This is useful when maintaining a combined count across different object paths.
 */
export const removeFromList = (key, index, pathForCount) => formSlice.actions.updateList({
  key,
  index,
  type: 'remove',
  pathForCount: pathForCount
})

export const onSegmentAttempted = (segment) => formSlice.actions.onSegmentAttempted({ segment })
export const { updateApplicantRelatedFields, resetForm, onSessionKilled } = formSlice.actions

export const selectSegments = state => {
  return Object.keys(state.form.segments)
    .reduce((o, i) => {
      const { fields, ...segmentMinusFields } = state.form.segments[i]
      return {
        ...o,
        [i]: segmentMinusFields
      }
    }, {})
}

export const formReducer = formSlice.reducer